import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { CalendarView } from '../components/CalendarView';
import { Textarea } from '../components/ui/Textarea';
import { DatePicker } from '../components/ui/DatePicker';
import { Plus, Filter, Eye, Edit, Search, Bell, Calendar as CalendarIcon, List, Clock, CheckCircle2, X, Save, Trash2, AlertCircle } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { motion, AnimatePresence } from 'framer-motion';
// Mock data
const initialDeadlinesData = Array.from({
  length: 50
}, (_, i) => {
  const types = ['Esempio 1', 'Esempio 2', 'Esempio 3', 'Esempio 4', 'Amministrativa'];
  const priorities = ['high', 'medium', 'low'];
  const statuses = ['In scadenza', 'Urgente', 'Completata', 'Pianificata'];
  const today = new Date();
  const day = i % 28 + 1;
  const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  return {
    id: String(i + 1),
    code: `SCA-2025/${String(i + 1).padStart(3, '0')}`,
    description: i % 3 === 0 ? `Rinnovo DVR ${i}` : `Corso Aggiornamento ${i}`,
    type: types[i % types.length],
    date: dateStr,
    client: `Azienda ${i} SRL`,
    notifyClient: i % 2 === 0 ? 'Sì' : 'No',
    status: statuses[i % statuses.length],
    priority: priorities[i % priorities.length] as 'high' | 'medium' | 'low',
    responsible: i % 4 === 0 ? 'Alessio' : i % 4 === 1 ? 'Lorenzo' : i % 4 === 2 ? 'Gian Franco' : 'Tiziano'
  };
});
const columnsConfig = [{
  key: 'code',
  label: 'Codice'
}, {
  key: 'description',
  label: 'Descrizione'
}, {
  key: 'type',
  label: 'Tipologia'
}, {
  key: 'client',
  label: 'Cliente'
}, {
  key: 'date',
  label: 'Data scadenza'
}, {
  key: 'notifyClient',
  label: 'Notifica'
}, {
  key: 'responsible',
  label: 'Responsabile'
}, {
  key: 'status',
  label: 'Stato'
}, {
  key: 'actions',
  label: 'Azioni'
}];
export function DeadlinesPage() {
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('calendar');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [recurrenceMode, setRecurrenceMode] = useState<'single' | 'multiple' | 'recurring'>('single');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const {
    addNotification
  } = useNotifications();
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(initialDeadlinesData);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filterResult, ['description', 'client', 'type', 'code']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);
  const handleView = (item: any) => {
    setSelectedItem(item);
    setIsViewModalOpen(true);
  };
  const handleEdit = (item: any) => {
    setSelectedItem(item);
    setFormData(item);
    setIsModalOpen(true);
  };
  const handleDelete = (item: any) => {
    setSelectedItem(item);
    setIsDeleteConfirmOpen(true);
  };
  const confirmDelete = () => {
    setDeleteSuccess(true);
    setTimeout(() => {
      setDeleteSuccess(false);
      setIsDeleteConfirmOpen(false);
      addNotification('success', 'Scadenza eliminata', 'La scadenza è stata eliminata con successo.');
    }, 1500);
  };
  const handleCreate = () => {
    setSelectedItem(null);
    const nextCode = `SCA-2025/${String(initialDeadlinesData.length + 1).padStart(3, '0')}`;
    setFormData({
      code: nextCode,
      notifyClient: false,
      notifyRef: false
    });
    setRecurrenceMode('single');
    setIsModalOpen(true);
  };
  const handleSave = () => {
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setIsModalOpen(false);
      addNotification('success', 'Scadenza salvata', 'La scadenza è stata salvata correttamente.');
    }, 2000);
  };
  const handleEventClick = (event: any) => {
    const item = initialDeadlinesData.find(d => d.id === event.id);
    if (item) {
      handleView(item);
    }
  };
  const hasActiveFilters = Object.keys(filters).some(k => filters[k]);
  return <Layout data-id="element-1846">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-1847">
        <div data-id="element-1848">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-1849">Scadenziario</h1>
          <p className="text-slate-500 mt-1" data-id="element-1850">
            Gestione scadenze tecniche e normative.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-1851">
          <div className="bg-slate-100 p-1 rounded-xl flex items-center" data-id="element-1852">
            <button onClick={() => setViewMode('calendar')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'calendar' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1853">
              <CalendarIcon className="h-4 w-4 inline-block mr-1.5" data-id="element-1854" />{' '}
              Calendario
            </button>
            <button onClick={() => setViewMode('list')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'list' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1855">
              <List className="h-4 w-4 inline-block mr-1.5" data-id="element-1856" /> Lista
            </button>
          </div>
          <Button onClick={handleCreate} startContent={<Plus className="h-4 w-4" data-id="element-1858" />} data-id="element-1857">
            Nuova scadenza
          </Button>
        </div>
      </div>

      {viewMode === 'list' ? <>
          <Card className="mb-6" noPadding data-id="element-1859">
            <div className="p-4 flex flex-col gap-4" data-id="element-1860">
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-1861">
                <div className="flex-1" data-id="element-1862">
                  <Input placeholder="Cerca scadenza..." startContent={<Search className="h-4 w-4" data-id="element-1864" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-1863" />
                </div>
                <div className="flex gap-2" data-id="element-1865">
                  <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-1867" />} data-id="element-1866">
                    Filtri{' '}
                    {hasActiveFilters && <Badge variant="primary" size="sm" className="ml-1" data-id="element-1868">
                        {Object.keys(filters).filter(k => filters[k]).length}
                      </Badge>}
                  </Button>
                  <ColumnCustomizer columns={columnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="deadlines" data-id="element-1869" />
                </div>
              </div>

              {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-1870">
                  <Select label="Tipologia" value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} options={[{
              value: '',
              label: 'Tutte'
            }, {
              value: 'Esempio 1',
              label: 'Esempio 1'
            }, {
              value: 'Esempio 2',
              label: 'Esempio 2'
            }, {
              value: 'Esempio 3',
              label: 'Esempio 3'
            }, {
              value: 'Esempio 4',
              label: 'Esempio 4'
            }, {
              value: 'Amministrativa',
              label: 'Amministrativa'
            }]} data-id="element-1871" />
                  <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
              value: '',
              label: 'Tutti'
            }, {
              value: 'In scadenza',
              label: 'In scadenza'
            }, {
              value: 'Urgente',
              label: 'Urgente'
            }, {
              value: 'Completata',
              label: 'Completata'
            }, {
              value: 'Pianificata',
              label: 'Pianificata'
            }]} data-id="element-1872" />
                  <Select label="Responsabile" value={filters.responsible || ''} onChange={e => setFilter('responsible', e.target.value)} options={[{
              value: '',
              label: 'Tutti'
            }, {
              value: 'Alessio',
              label: 'Alessio'
            }, {
              value: 'Lorenzo',
              label: 'Lorenzo'
            }, {
              value: 'Gian Franco',
              label: 'Gian Franco'
            }, {
              value: 'Tiziano',
              label: 'Tiziano'
            }]} data-id="element-1873" />
                  <Select label="Notifica" value={filters.notifyClient || ''} onChange={e => setFilter('notifyClient', e.target.value)} options={[{
              value: '',
              label: 'Tutte'
            }, {
              value: 'Sì',
              label: 'Sì'
            }, {
              value: 'No',
              label: 'No'
            }]} data-id="element-1874" />
                  <div className="col-span-full flex justify-end" data-id="element-1875">
                    <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-1877" />} data-id="element-1876">
                      Resetta filtri
                    </Button>
                  </div>
                </div>}
            </div>
          </Card>

          <Card noPadding className="overflow-hidden" data-id="element-1878">
            <div className="overflow-x-auto" data-id="element-1879">
              <table className="w-full text-left border-collapse table-fixed" data-id="element-1880">
                <thead data-id="element-1881">
                  <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-1882">
                    {columnsConfig.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className={`px-6 py-4 ${col.key === 'actions' ? 'text-left w-32' : ''}`} data-id="element-1883">
                          {col.label}
                        </th>)}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-id="element-1884">
                  {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-1885">
                      {visibleColumns.includes('code') && <td className="px-6 py-4 font-mono text-xs text-slate-500" data-id="element-1886">
                          {item.code}
                        </td>}
                      {visibleColumns.includes('description') && <td className="px-6 py-4 font-medium text-slate-900" data-id="element-1887">
                          {item.description}
                        </td>}
                      {visibleColumns.includes('type') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-1888">
                          {item.type}
                        </td>}
                      {visibleColumns.includes('client') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-1889">
                          {item.client}
                        </td>}
                      {visibleColumns.includes('date') && <td className="px-6 py-4 text-sm font-mono text-slate-600" data-id="element-1890">
                          {item.date}
                        </td>}
                      {visibleColumns.includes('notifyClient') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-1891">
                          {item.notifyClient}
                        </td>}
                      {visibleColumns.includes('responsible') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-1892">
                          {item.responsible}
                        </td>}
                      {visibleColumns.includes('status') && <td className="px-6 py-4" data-id="element-1893">
                          <Badge variant={item.status === 'Urgente' ? 'danger' : item.status === 'In scadenza' ? 'warning' : item.status === 'Completata' ? 'success' : 'info'} data-id="element-1894">
                            {item.status}
                          </Badge>
                        </td>}
                      {visibleColumns.includes('actions') && <td className="px-6 py-4" data-id="element-1895">
                          <div className="flex items-center gap-2" data-id="element-1896">
                            <Button variant="light" size="sm" className="!px-2" onClick={() => handleView(item)} title="Visualizza scadenza" data-id="element-1897">
                              <Eye className="h-4 w-4 text-slate-500" data-id="element-1898" />
                            </Button>
                            <Button variant="light" size="sm" className="!px-2" onClick={() => handleEdit(item)} title="Modifica scadenza" data-id="element-1899">
                              <Edit className="h-4 w-4 text-seform-orange" data-id="element-1900" />
                            </Button>
                            <Button variant="light" size="sm" className="!px-2" onClick={() => handleDelete(item)} title="Elimina scadenza" data-id="element-1901">
                              <Trash2 className="h-4 w-4 text-red-500" data-id="element-1902" />
                            </Button>
                          </div>
                        </td>}
                    </tr>)}
                </tbody>
              </table>
            </div>
            <div className="border-t border-slate-100" data-id="element-1903">
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-1904" />
            </div>
          </Card>
        </> : <CalendarView events={searchResult.map(item => ({
      id: item.id,
      title: item.description,
      date: item.date,
      type: item.type,
      priority: item.priority,
      client: item.client
    }))} onEventClick={handleEventClick} data-id="element-1905" />}

      {/* View Modal */}
      <Modal isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} title="Dettaglio scadenza" size="lg" data-id="element-1906">
        {selectedItem && <div className="space-y-6" data-id="element-1907">
            <div className="grid grid-cols-2 gap-4" data-id="element-1908">
              <div data-id="element-1909">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1910">
                  Codice
                </span>
                <p className="font-mono font-medium" data-id="element-1911">{selectedItem.code}</p>
              </div>
              <div data-id="element-1912">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1913">
                  Stato
                </span>
                <div className="mt-1" data-id="element-1914">
                  <Badge variant={selectedItem.status === 'Urgente' ? 'danger' : selectedItem.status === 'In scadenza' ? 'warning' : selectedItem.status === 'Completata' ? 'success' : 'info'} data-id="element-1915">
                    {selectedItem.status}
                  </Badge>
                </div>
              </div>
              <div className="col-span-full" data-id="element-1916">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1917">
                  Descrizione
                </span>
                <p className="font-medium" data-id="element-1918">{selectedItem.description}</p>
              </div>
              <div data-id="element-1919">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1920">
                  Tipologia
                </span>
                <p className="font-medium" data-id="element-1921">{selectedItem.type}</p>
              </div>
              <div data-id="element-1922">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1923">
                  Cliente
                </span>
                <p className="font-medium" data-id="element-1924">{selectedItem.client}</p>
              </div>
              <div data-id="element-1925">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1926">
                  Data scadenza
                </span>
                <p className="font-mono font-medium" data-id="element-1927">{selectedItem.date}</p>
              </div>
              <div data-id="element-1928">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1929">
                  Responsabile
                </span>
                <p className="font-medium" data-id="element-1930">{selectedItem.responsible}</p>
              </div>
              <div data-id="element-1931">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-1932">
                  Notifica cliente
                </span>
                <p className="font-medium" data-id="element-1933">{selectedItem.notifyClient}</p>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-1934">
              <Button variant="flat" color="default" onClick={() => setIsViewModalOpen(false)} data-id="element-1935">
                Chiudi
              </Button>
              <Button onClick={() => {
            setIsViewModalOpen(false);
            handleEdit(selectedItem);
          }} startContent={<Edit className="h-4 w-4" data-id="element-1937" />} data-id="element-1936">
                Modifica
              </Button>
            </div>
          </div>}
      </Modal>

      {/* Create/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedItem ? 'Modifica scadenza' : 'Nuova scadenza'} size="lg" data-id="element-1938">
        <AnimatePresence mode="wait" data-id="element-1939">
          {saveSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3
        }} className="flex flex-col items-center justify-center py-16" data-id="element-1940">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 150,
            damping: 15,
            delay: 0.1
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-1941">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-1942" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-1943">
                Scadenza salvata con successo
              </p>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-1944">
              <div className="space-y-6" data-id="element-1945">
                <section data-id="element-1946">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-1947">
                    <Clock className="h-4 w-4" data-id="element-1948" /> Dati base
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1949">
                    <Input label="Codice" value={formData.code || ''} onChange={e => setFormData({
                  ...formData,
                  code: e.target.value
                })} data-id="element-1950" />
                    <Select label="Tipologia" value={formData.type || ''} onChange={e => setFormData({
                  ...formData,
                  type: e.target.value
                })} options={[{
                  value: 'Esempio 1',
                  label: 'Esempio 1'
                }, {
                  value: 'Esempio 2',
                  label: 'Esempio 2'
                }, {
                  value: 'Esempio 3',
                  label: 'Esempio 3'
                }, {
                  value: 'Esempio 4',
                  label: 'Esempio 4'
                }, {
                  value: 'Amministrativa',
                  label: 'Amministrativa'
                }]} required data-id="element-1951" />
                    <div className="col-span-full" data-id="element-1952">
                      <Textarea label="Descrizione" value={formData.description || ''} onChange={e => setFormData({
                    ...formData,
                    description: e.target.value
                  })} required data-id="element-1953" />
                    </div>
                    <Select label="Cliente" value={formData.client || ''} onChange={e => setFormData({
                  ...formData,
                  client: e.target.value
                })} options={[{
                  value: 'Azienda 0 SRL',
                  label: 'Azienda 0 SRL'
                }, {
                  value: 'Azienda 1 SRL',
                  label: 'Azienda 1 SRL'
                }, {
                  value: 'Azienda 2 SRL',
                  label: 'Azienda 2 SRL'
                }]} required data-id="element-1954" />
                    <Input label="Responsabile" value={formData.responsible || ''} onChange={e => setFormData({
                  ...formData,
                  responsible: e.target.value
                })} data-id="element-1955" />
                  </div>
                </section>

                <section data-id="element-1956">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-1957">
                    <CalendarIcon className="h-4 w-4" data-id="element-1958" /> Date e ripetizioni
                  </h4>
                  <div className="flex gap-4 mb-4" data-id="element-1959">
                    <label className="flex items-center gap-2 text-sm cursor-pointer" data-id="element-1960">
                      <input type="radio" name="recurrence" checked={recurrenceMode === 'single'} onChange={() => setRecurrenceMode('single')} className="text-seform-orange focus:ring-seform-orange" data-id="element-1961" />
                      Data singola
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer" data-id="element-1962">
                      <input type="radio" name="recurrence" checked={recurrenceMode === 'multiple'} onChange={() => setRecurrenceMode('multiple')} className="text-seform-orange focus:ring-seform-orange" data-id="element-1963" />
                      Date multiple
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer" data-id="element-1964">
                      <input type="radio" name="recurrence" checked={recurrenceMode === 'recurring'} onChange={() => setRecurrenceMode('recurring')} className="text-seform-orange focus:ring-seform-orange" data-id="element-1965" />
                      Periodo ripetuto
                    </label>
                  </div>

                  {recurrenceMode === 'single' && <DatePicker label="Data scadenza" value={formData.date || ''} onChange={e => setFormData({
                ...formData,
                date: e.target.value
              })} data-id="element-1966" />}

                  {recurrenceMode === 'multiple' && <div className="space-y-2" data-id="element-1967">
                      <p className="text-xs text-slate-500" data-id="element-1968">
                        Aggiungi più date per questa scadenza
                      </p>
                      <div className="flex gap-2" data-id="element-1969">
                        <DatePicker data-id="element-1970" />
                        <Button size="sm" variant="flat" startContent={<Plus className="h-4 w-4" data-id="element-1972" />} data-id="element-1971">
                          Aggiungi
                        </Button>
                      </div>
                    </div>}

                  {recurrenceMode === 'recurring' && <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg border border-slate-100" data-id="element-1973">
                      <DatePicker label="Data inizio" data-id="element-1974" />
                      <div className="flex gap-2 items-end" data-id="element-1975">
                        <Input label="Ripeti ogni" type="number" defaultValue="1" data-id="element-1976" />
                        <Select options={[{
                    value: 'M',
                    label: 'Mesi'
                  }, {
                    value: 'Y',
                    label: 'Anni'
                  }]} defaultValue="Y" data-id="element-1977" />
                      </div>
                      <Input label="Per (volte)" type="number" defaultValue="5" data-id="element-1978" />
                      <div className="col-span-full pt-2" data-id="element-1979">
                        <p className="text-xs font-medium text-slate-600 mb-1" data-id="element-1980">
                          Anteprima occorrenze:
                        </p>
                        <div className="flex gap-2 flex-wrap" data-id="element-1981">
                          <Badge variant="neutral" data-id="element-1982">10/05/2024</Badge>
                          <Badge variant="neutral" data-id="element-1983">10/05/2025</Badge>
                          <Badge variant="neutral" data-id="element-1984">10/05/2026</Badge>
                        </div>
                      </div>
                    </div>}
                </section>

                <section data-id="element-1985">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-1986">
                    <Bell className="h-4 w-4" data-id="element-1987" /> Notifiche
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1988">
                    <div className="flex items-center gap-2" data-id="element-1989">
                      <input type="checkbox" id="notifyClient" className="rounded text-seform-orange focus:ring-seform-orange" checked={formData.notifyClient || false} onChange={e => setFormData({
                    ...formData,
                    notifyClient: e.target.checked
                  })} data-id="element-1990" />
                      <label htmlFor="notifyClient" className="text-sm text-slate-700" data-id="element-1991">
                        Invia notifica al cliente
                      </label>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-1992">
                      <input type="checkbox" id="notifyRef" className="rounded text-seform-orange focus:ring-seform-orange" checked={formData.notifyRef || false} onChange={e => setFormData({
                    ...formData,
                    notifyRef: e.target.checked
                  })} data-id="element-1993" />
                      <label htmlFor="notifyRef" className="text-sm text-slate-700" data-id="element-1994">
                        Invia notifica referente SEF
                      </label>
                    </div>
                  </div>
                </section>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-1995">
                  <Button variant="flat" color="default" onClick={() => setIsModalOpen(false)} data-id="element-1996">
                    Annulla
                  </Button>
                  <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-1998" />} data-id="element-1997">
                    Salva scadenza
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)} title="Conferma eliminazione" size="sm" data-id="element-1999">
        <AnimatePresence mode="wait" data-id="element-2000">
          {deleteSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3
        }} className="flex flex-col items-center justify-center py-12" data-id="element-2001">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 150,
            damping: 15,
            delay: 0.1
          }} className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-2002">
                <CheckCircle2 className="w-10 h-10 text-green-500" data-id="element-2003" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-2004">
                Eliminato con successo
              </p>
            </motion.div> : <motion.div key="confirm" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-2005">
              <div className="space-y-6" data-id="element-2006">
                <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex gap-3" data-id="element-2007">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" data-id="element-2008" />
                  <p className="text-sm text-red-700" data-id="element-2009">
                    Attenzione: questa azione è irreversibile. La scadenza verrà
                    eliminata definitivamente.
                  </p>
                </div>
                <div className="flex justify-end gap-3 pt-4" data-id="element-2010">
                  <Button variant="flat" color="default" onClick={() => setIsDeleteConfirmOpen(false)} data-id="element-2011">
                    Annulla
                  </Button>
                  <Button color="danger" onClick={confirmDelete} data-id="element-2012">
                    Elimina
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>
    </Layout>;
}